import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { ChevronLeft, ChevronRight, RefreshCw, Sparkles, Clock, UserCog, Copy } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { CalendarioTurni } from "../../components/turni/CalendarioTurni";
import { AssegnaSlotDialog } from "../../components/turni/AssegnaSlotDialog";
import { GeneraTurniDialog } from "../../components/turni/GeneraTurniDialog";
import { FasceTurnoDialogs } from "../../components/turni/FasceTurnoDialogs";
import { PreferenzeDialogs } from "../../components/turni/PreferenzeDialogs";

const API = process.env.REACT_APP_BACKEND_URL;
const GIORNI_NOMI = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const GIORNI_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const COLORI_REPARTO = {
  'cucina': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-400', dot: 'bg-orange-500', label: 'Cucina' },
  'sala': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-400', dot: 'bg-blue-500', label: 'Sala' },
  'laboratorio': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-400', dot: 'bg-purple-500', label: 'Lab' },
  'altro': { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-400', dot: 'bg-gray-500', label: 'Altro' }
};

const TurniMobile = () => {
  const { user, token, localeCorrente } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [settimanaOffset, setSettimanaOffset] = useState(0);
  const [settimanaInizio, setSettimanaInizio] = useState("");
  const [giorni, setGiorni] = useState([]);
  const oggi = new Date().toISOString().split("T")[0];

  const [fasce, setFasce] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [turni, setTurni] = useState([]);
  const [preferenze, setPreferenze] = useState({});
  const [assenzeSettimana, setAssenzeSettimana] = useState([]);

  // Dialog states
  const [dialogAssegna, setDialogAssegna] = useState(false);
  const [slotSelezionato, setSlotSelezionato] = useState(null);
  const [operatoriSelezionatiSlot, setOperatoriSelezionatiSlot] = useState([]);
  const [dialogPreferenze, setDialogPreferenze] = useState(false);
  const [dialogPreferenzeOperatori, setDialogPreferenzeOperatori] = useState(false);
  const [dialogGenera, setDialogGenera] = useState(false);
  const [operatorePreferenze, setOperatorePreferenze] = useState(null);
  const [prefForm, setPrefForm] = useState({});
  const [dialogFasce, setDialogFasce] = useState(false);
  const [dialogEditFascia, setDialogEditFascia] = useState(false);
  const [fasciaEdit, setFasciaEdit] = useState(null);
  const [fasciaForm, setFasciaForm] = useState({ nome: "", ora_inizio: "09:00", ora_fine: "13:00", colore: "#3B82F6", reparto: "tutti", giorni_attivi: null });
  const [generando, setGenerando] = useState(false);
  const [anteprimaGenerazione, setAnteprimaGenerazione] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [vistaTab, setVistaTab] = useState("turni");
  const [conflittoDialog, setConflittoDialog] = useState(null);
  const [dialogCustomTurno, setDialogCustomTurno] = useState(false);
  const [customTurnoForm, setCustomTurnoForm] = useState({ data: "", utente_id: "", ora_inizio: "09:00", ora_fine: "17:00", reparto: "tutti", note: "" });
  const [salvandoCustom, setSalvandoCustom] = useState(false);

  // Copia turni da settimana scorsa
  const [copiaSettimanaDialog, setCopiaSettimanaDialog] = useState(null); // { dst_count, src_count }
  const [copiando, setCopiando] = useState(false);

  // Week calculation
  useEffect(() => {
    const fmtDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const g = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${g}`;
    };
    const oggi = new Date();
    const giornoSettimana = oggi.getDay();
    const diff = giornoSettimana === 0 ? -6 : 1 - giornoSettimana;
    const lunedi = new Date(oggi);
    lunedi.setDate(oggi.getDate() + diff + (settimanaOffset * 7));
    const giorniArr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(lunedi);
      d.setDate(lunedi.getDate() + i);
      giorniArr.push({ data: fmtDate(d), giorno: d.getDate(), mese: d.toLocaleDateString("it-IT", { month: "short" }), nome: GIORNI_NOMI[i], nomeShort: GIORNI_SHORT[i], idx: i });
    }
    setSettimanaInizio(fmtDate(lunedi));
    setGiorni(giorniArr);
  }, [settimanaOffset]);

  const settimanaFine = giorni.length > 0 ? giorni[6]?.data : "";

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!localeCorrente?.id || !token || !settimanaInizio || !settimanaFine) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const localeParam = `locale_id=${localeCorrente.id}`;
      const [fasceRes, turniRes, prefRes, assenzeRes] = await Promise.all([
        axios.get(`${API}/api/personale/fasce-turno?${localeParam}`, { headers }),
        axios.get(`${API}/api/personale/turni?${localeParam}&data_inizio=${settimanaInizio}&data_fine=${settimanaFine}`, { headers }),
        axios.get(`${API}/api/personale/preferenze-turni?${localeParam}`, { headers }),
        axios.get(`${API}/api/personale/assenze-settimana?${localeParam}&data_inizio=${settimanaInizio}`, { headers }).catch(() => ({ data: [] }))
      ]);
      let operatoriData = [];
      try { operatoriData = (await axios.get(`${API}/api/personale/utenti-turni?${localeParam}`, { headers })).data; }
      catch { operatoriData = prefRes.data.map(p => ({ id: p.utente_id, nome: p.utente_nome })); }
      setFasce(fasceRes.data.filter(f => f.attivo !== false));
      setOperatori(operatoriData);
      setTurni(turniRes.data);
      const prefMap = {};
      prefRes.data.forEach(p => prefMap[p.utente_id] = p);
      setPreferenze(prefMap);
      setAssenzeSettimana(assenzeRes.data || []);
    } catch { toast.error("Errore nel caricamento dati"); }
    finally { setLoading(false); }
  }, [localeCorrente?.id, token, settimanaInizio, settimanaFine]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getTurniPerSlot = (data, fasciaId) => turni.filter(t => t.data === data && t.fascia_turno_id === fasciaId);

  // Slot assignment handlers
  const openSlotDialog = (data, fascia) => {
    const turniSlot = turni.filter(t => t.data === data && t.fascia_turno_id === fascia.id);
    setSlotSelezionato({ data, fascia, turniEsistenti: turniSlot });
    setOperatoriSelezionatiSlot(turniSlot.map(t => t.utente_id));
    setDialogAssegna(true);
  };

  const handleSalvaAssegnazioni = async () => {
    if (!slotSelezionato) return;
    const { data, fascia, turniEsistenti } = slotSelezionato;
    try {
      const daRimuovere = turniEsistenti.filter(t => !operatoriSelezionatiSlot.includes(t.utente_id));
      for (const turno of daRimuovere) await axios.delete(`${API}/api/personale/turni/${turno.id}?locale_id=${localeCorrente.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const idsEsistenti = turniEsistenti.map(t => t.utente_id);
      const daAggiungere = operatoriSelezionatiSlot.filter(id => !idsEsistenti.includes(id));
      for (const opId of daAggiungere) {
        try {
          await axios.post(`${API}/api/personale/turni?locale_id=${localeCorrente.id}&created_by=${user.id}`, { utente_id: opId, fascia_turno_id: fascia.id, data }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (err) {
          const errDetail = err.response?.data?.detail;
          if (typeof errDetail === 'object' && errDetail?.conflitti) {
            const c = errDetail.conflitti[0];
            if (c?.tipo === 'turno_sovrapposto' && c?.turno_id_conflitto) {
              const opNome = operatori.find(o => o.id === opId)?.nome || opId;
              setConflittoDialog({
                operatoreId: opId,
                operatoreNome: opNome,
                fasciaNew: fascia,
                data,
                conflitto: c
              });
              return;
            }
          }
          throw err;
        }
      }
      toast.success("Turni aggiornati");
      setDialogAssegna(false);
      fetchData();
    } catch (error) {
      const errDetail = error.response?.data?.detail;
      if (typeof errDetail === 'object' && errDetail?.conflitti) toast.error(`Conflitto: ${errDetail.conflitti[0]?.messaggio || 'Turno già assegnato'}`);
      else if (typeof errDetail === 'string') toast.error(errDetail);
      else toast.error("Errore nel salvataggio");
    }
  };

  const handleRisolviConflitto = async (azione) => {
    if (!conflittoDialog) return;
    const { operatoreId, fasciaNew, data, conflitto } = conflittoDialog;
    try {
      if (azione === 'sostituisci') {
        await axios.delete(`${API}/api/personale/turni/${conflitto.turno_id_conflitto}?locale_id=${localeCorrente.id}`, { headers: { Authorization: `Bearer ${token}` } });
        await axios.post(`${API}/api/personale/turni?locale_id=${localeCorrente.id}&created_by=${user.id}`, { utente_id: operatoreId, fascia_turno_id: fasciaNew.id, data }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Turno sostituito");
      } else {
        toast.info("Turno esistente mantenuto");
      }
      setConflittoDialog(null);
      setDialogAssegna(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nella gestione del conflitto");
    }
  };

  const handleCancellaTurniSettimana = async () => {
    if (!await confirm(`Eliminare i turni futuri della settimana ${giorni[0]?.giorno} ${giorni[0]?.mese} - ${giorni[6]?.giorno} ${giorni[6]?.mese}?`)) return;
    try {
      const res = await axios.delete(`${API}/api/personale/turni-settimana?locale_id=${localeCorrente.id}&data_inizio=${settimanaInizio}&data_fine=${settimanaFine}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.protetti > 0 ? `Eliminati ${res.data.cancellati} turni (${res.data.protetti} protetti)` : `Eliminati ${res.data.cancellati} turni`);
      fetchData();
    } catch { toast.error("Errore nella cancellazione"); }
  };

  const openCustomTurnoDialog = (data) => {
    setCustomTurnoForm({ data, utente_id: "", ora_inizio: "09:00", ora_fine: "17:00", reparto: "tutti", note: "" });
    setDialogCustomTurno(true);
  };

  const handleSalvaCustomTurno = async () => {
    if (!customTurnoForm.utente_id) { toast.error("Seleziona un operatore"); return; }
    if (!customTurnoForm.ora_inizio || !customTurnoForm.ora_fine) { toast.error("Inserisci orario inizio e fine"); return; }
    if (customTurnoForm.ora_fine <= customTurnoForm.ora_inizio) { toast.error("L'orario di fine deve essere dopo l'inizio"); return; }
    setSalvandoCustom(true);
    try {
      await axios.post(
        `${API}/api/personale/turni/custom?locale_id=${localeCorrente.id}&created_by=${user?.id || "admin"}`,
        customTurnoForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Turno aggiunto");
      setDialogCustomTurno(false);
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.conflitti) toast.error(`Conflitto: ${detail.conflitti[0]?.fascia_nome_conflitto || "orari sovrapposti"}`);
      else toast.error(detail || "Errore nel salvataggio");
    } finally {
      setSalvandoCustom(false);
    }
  };

  // Preferences handlers
  const openPreferenze = (operatore) => {
    const pref = preferenze[operatore.id] || { giorni_riposo: 1, giorni_riposo_disponibili: [5, 6], turni_esclusi: [] };
    if (!pref.giorni_riposo_disponibili) {
      const giorni_esclusi = pref.giorni_esclusi || [];
      const giorni_lav = pref.giorni_lavorativi || [];
      pref.giorni_riposo_disponibili = giorni_esclusi.length > 0 ? giorni_esclusi : (giorni_lav.length > 0 ? [0,1,2,3,4,5,6].filter(g => !giorni_lav.includes(g)) : [5, 6]);
    }
    if (!pref.giorni_riposo) pref.giorni_riposo = pref.giorni_riposo_disponibili?.length || 1;
    setOperatorePreferenze(operatore);
    setPrefForm(pref);
    setDialogPreferenze(true);
  };

  const handleSavePreferenze = async () => {
    try {
      await axios.post(`${API}/api/personale/preferenze-turni?locale_id=${localeCorrente.id}`, { utente_id: operatorePreferenze.id, giorni_riposo: prefForm.giorni_riposo || 1, giorni_riposo_disponibili: prefForm.giorni_riposo_disponibili || [5, 6], turni_esclusi: prefForm.turni_esclusi || [] }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Preferenze salvate");
      setDialogPreferenze(false);
      fetchData();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  // Fasce handlers
  const openNuovaFascia = () => {
    setFasciaEdit(null);
    setFasciaForm({ nome: "", ora_inizio: "09:00", ora_fine: "13:00", colore: "#3B82F6", reparto: "tutti", giorni_attivi: null });
    setDialogEditFascia(true);
  };

  const openModificaFascia = (fascia) => {
    setFasciaEdit(fascia);
    setFasciaForm({ nome: fascia.nome, ora_inizio: fascia.ora_inizio, ora_fine: fascia.ora_fine, colore: fascia.colore || "#3B82F6", reparto: fascia.reparto || "tutti", giorni_attivi: fascia.giorni_attivi || null });
    setDialogEditFascia(true);
  };

  const toggleGiornoFascia = (giornoIdx) => {
    let nuoviGiorni = fasciaForm.giorni_attivi ? [...fasciaForm.giorni_attivi] : [0,1,2,3,4,5,6];
    if (nuoviGiorni.includes(giornoIdx)) nuoviGiorni = nuoviGiorni.filter(g => g !== giornoIdx);
    else { nuoviGiorni.push(giornoIdx); nuoviGiorni.sort(); }
    setFasciaForm({ ...fasciaForm, giorni_attivi: nuoviGiorni.length === 7 ? null : nuoviGiorni });
  };

  const handleSaveFascia = async () => {
    if (!fasciaForm.nome || !fasciaForm.ora_inizio || !fasciaForm.ora_fine) { toast.error("Compila tutti i campi obbligatori"); return; }
    try {
      if (fasciaEdit) await axios.put(`${API}/api/personale/fasce-turno/${fasciaEdit.id}?locale_id=${localeCorrente.id}`, fasciaForm, { headers: { Authorization: `Bearer ${token}` } });
      else await axios.post(`${API}/api/personale/fasce-turno?locale_id=${localeCorrente.id}`, fasciaForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(fasciaEdit ? "Fascia modificata" : "Fascia creata");
      setDialogEditFascia(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Errore nel salvataggio"); }
  };

  const handleDeleteFascia = async (fasciaId) => {
    if (!await confirm("Eliminare questa fascia turno?")) return;
    try { await axios.delete(`${API}/api/personale/fasce-turno/${fasciaId}?locale_id=${localeCorrente.id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Fascia eliminata"); fetchData(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  // Generation handlers
  const handleGenera = async () => {
    setGenerando(true);
    try {
      const res = await axios.post(`${API}/api/personale/genera-turni?locale_id=${localeCorrente.id}`, { settimana_inizio: settimanaInizio, usa_storico: true, settimane_analisi: 4 }, { headers: { Authorization: `Bearer ${token}` } });
      setAnteprimaGenerazione(res.data);
      setDialogGenera(true);
    } catch { toast.error("Errore nella generazione"); }
    finally { setGenerando(false); }
  };

  const handleSalvaGenerati = async () => {
    if (!anteprimaGenerazione?.turni_generati) return;
    setSalvando(true);
    try {
      await axios.post(`${API}/api/personale/salva-turni-generati?locale_id=${localeCorrente.id}`, anteprimaGenerazione.turni_generati, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Turni salvati!");
      setDialogGenera(false);
      setAnteprimaGenerazione(null);
      fetchData();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSalvando(false); }
  };

  // ============================================
  // COPIA SETTIMANA SCORSA
  // ============================================
  const getSettimanaScorsaInizio = () => {
    if (!settimanaInizio) return null;
    const d = new Date(settimanaInizio + "T00:00:00");
    d.setDate(d.getDate() - 7);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const g = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${g}`;
  };

  const handleApriCopiaSettimana = async () => {
    const fromWeek = getSettimanaScorsaInizio();
    if (!fromWeek || !localeCorrente?.id) return;
    try {
      const res = await axios.get(
        `${API}/api/personale/turni/copia-settimana/preview?locale_id=${localeCorrente.id}&from_week_start=${fromWeek}&to_week_start=${settimanaInizio}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { sorgente, destinazione } = res.data;
      if (sorgente === 0) {
        toast.error("Nessun turno nella settimana scorsa da copiare");
        return;
      }
      if (destinazione === 0) {
        // Nessun conflitto: copia diretta in modalità aggiungi
        await eseguiCopia("aggiungi", fromWeek, sorgente);
      } else {
        setCopiaSettimanaDialog({ fromWeek, sorgente, destinazione });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel calcolo anteprima");
    }
  };

  const eseguiCopia = async (mode, fromWeek = null, sorgente = null) => {
    const fw = fromWeek || copiaSettimanaDialog?.fromWeek || getSettimanaScorsaInizio();
    if (!fw) return;
    setCopiando(true);
    try {
      const res = await axios.post(
        `${API}/api/personale/turni/copia-settimana`,
        {
          locale_id: localeCorrente.id,
          from_week_start: fw,
          to_week_start: settimanaInizio,
          mode,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { copiati, saltati, cancellati, protetti } = res.data;
      let msg = `${copiati} turni copiati`;
      if (saltati > 0) msg += `, ${saltati} già presenti saltati`;
      if (cancellati > 0) msg += `, ${cancellati} sovrascritti`;
      if (protetti > 0) msg += `, ${protetti} protetti (timbrati)`;
      if (copiati === 0) toast.message(msg);
      else toast.success(msg);
      setCopiaSettimanaDialog(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella copia");
    } finally {
      setCopiando(false);
    }
  };

  if (loading && !giorni.length) return <div className="loading-state"><div className="text-muted-custom">Caricamento...</div></div>;

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="turni-mobile-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h1 className="page-title text-lg">Gestione Turni</h1>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => setDialogFasce(true)} className="border-border" data-testid="impostazioni-turni-btn" title="Fasce Turno"><Clock className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" onClick={() => setDialogPreferenzeOperatori(true)} className="border-border" data-testid="preferenze-operatori-btn" title="Preferenze Operatori"><UserCog className="h-4 w-4" /></Button>
              <Button onClick={handleGenera} disabled={generando} className="bg-gradient-to-r from-violet-600 to-indigo-600" data-testid="genera-turni-btn">
                {generando ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}Genera
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between bg-muted/50 rounded-lg p-2">
              <Button variant="ghost" size="icon" onClick={() => setSettimanaOffset(s => s - 1)} data-testid="prev-week-btn"><ChevronLeft className="h-5 w-5" /></Button>
              <div className="text-center" data-testid="week-display">
                <p className="font-medium">{giorni[0]?.giorno} {giorni[0]?.mese} - {giorni[6]?.giorno} {giorni[6]?.mese}</p>
                <p className="text-xs text-muted-foreground">{settimanaOffset === 0 ? "Questa settimana" : settimanaOffset === 1 ? "Prossima settimana" : settimanaOffset === -1 ? "Settimana scorsa" : ""}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSettimanaOffset(s => s + 1)} data-testid="next-week-btn"><ChevronRight className="h-5 w-5" /></Button>
            </div>

            {/* Pulsante Copia Settimana Scorsa */}
            <button
              onClick={handleApriCopiaSettimana}
              disabled={copiando || loading}
              className="mt-2 w-full h-10 rounded-lg bg-amber-50 hover:bg-amber-100 dark:bg-amber-500/10 dark:hover:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="copia-settimana-scorsa-btn"
              title="Copia tutti i turni della settimana precedente in questa settimana"
            >
              {copiando ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
              <span>Copia da settimana scorsa</span>
            </button>
        </div>

        <div className="px-4 pb-3">
            <div className="flex items-center gap-3 text-xs">
              <span className="text-muted-foreground">Reparti:</span>
              {Object.entries(COLORI_REPARTO).filter(([k]) => k !== 'altro').map(([key, val]) => (
                <div key={key} className="flex items-center gap-1"><div className={cn("w-2 h-2 rounded-full", val.dot)} /><span className={val.text}>{val.label}</span></div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Tocca uno slot per assegnare gli operatori</p>
          </div>
      </div>

      <CalendarioTurni
          giorni={giorni} fasce={fasce} turni={turni} operatori={operatori}
          assenzeSettimana={assenzeSettimana} oggi={oggi}
          settimanaFine={settimanaFine}
          onOpenSlot={openSlotDialog}
          onCancellaTurni={handleCancellaTurniSettimana}
          getTurniPerSlot={getTurniPerSlot}
          onAddCustomTurno={openCustomTurnoDialog}
          onDeleteTurno={async (turnoId) => {
            try {
              await axios.delete(`${API}/api/personale/turni/${turnoId}?locale_id=${localeCorrente.id}`, { headers: { Authorization: `Bearer ${token}` } });
              setTurni(prev => prev.filter(t => t.id !== turnoId));
              toast.success("Turno anomalo eliminato");
            } catch { toast.error("Errore nell'eliminazione"); }
          }}
        />

      <AssegnaSlotDialog open={dialogAssegna} onOpenChange={setDialogAssegna} slotSelezionato={slotSelezionato} giorni={giorni} operatori={operatori} operatoriSelezionati={operatoriSelezionatiSlot} onToggleOperatore={(id) => setOperatoriSelezionatiSlot(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])} onSalva={handleSalvaAssegnazioni} />
      <GeneraTurniDialog open={dialogGenera} onOpenChange={setDialogGenera} anteprimaGenerazione={anteprimaGenerazione} salvando={salvando} onSalva={handleSalvaGenerati} />
      <FasceTurnoDialogs dialogFasce={dialogFasce} onDialogFasceChange={setDialogFasce} dialogEditFascia={dialogEditFascia} onDialogEditFasciaChange={setDialogEditFascia} fasce={fasce} fasciaEdit={fasciaEdit} fasciaForm={fasciaForm} onFasciaFormChange={setFasciaForm} onOpenNuova={openNuovaFascia} onOpenModifica={openModificaFascia} onSave={handleSaveFascia} onDelete={handleDeleteFascia} onToggleGiorno={toggleGiornoFascia} />
      <PreferenzeDialogs dialogListOpen={dialogPreferenzeOperatori} onDialogListChange={setDialogPreferenzeOperatori} dialogEditOpen={dialogPreferenze} onDialogEditChange={setDialogPreferenze} operatori={operatori} preferenze={preferenze} fasce={fasce} operatorePreferenze={operatorePreferenze} prefForm={prefForm} onPrefFormChange={setPrefForm} onOpenPreferenze={openPreferenze} onSavePreferenze={handleSavePreferenze} onToggleTurnoEscluso={(fasciaId) => { const arr = [...(prefForm.turni_esclusi || [])]; const i = arr.indexOf(fasciaId); if (i >= 0) arr.splice(i, 1); else arr.push(fasciaId); setPrefForm({ ...prefForm, turni_esclusi: arr }); }} onToggleGiornoRiposo={(idx) => { const arr = [...(prefForm.giorni_riposo_disponibili || [5, 6])]; const i = arr.indexOf(idx); if (i >= 0) arr.splice(i, 1); else arr.push(idx); arr.sort((a, b) => a - b); setPrefForm({ ...prefForm, giorni_riposo_disponibili: arr }); }} />

      {/* Dialog Turno Personalizzato */}
      {dialogCustomTurno && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="custom-turno-overlay">
          <div className="w-full max-w-sm bg-card rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 border border-border">
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-foreground">Aggiungi turno</h3>
                <p className="text-sm text-muted-foreground">{customTurnoForm.data}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground mb-1 block">Operatore *</label>
                  <select
                    className="w-full h-10 rounded-lg border border-border bg-muted/30 px-3 text-sm text-foreground"
                    value={customTurnoForm.utente_id}
                    onChange={e => setCustomTurnoForm(f => ({ ...f, utente_id: e.target.value }))}
                    data-testid="custom-turno-operatore"
                  >
                    <option value="">Seleziona operatore…</option>
                    {operatori.map(op => (
                      <option key={op.id} value={op.id}>{op.nome} {op.cognome}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">Inizio *</label>
                    <input type="time" className="w-full h-10 rounded-lg border border-border bg-muted/30 px-3 text-sm text-foreground" value={customTurnoForm.ora_inizio} onChange={e => setCustomTurnoForm(f => ({ ...f, ora_inizio: e.target.value }))} data-testid="custom-turno-inizio" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">Fine *</label>
                    <input type="time" className="w-full h-10 rounded-lg border border-border bg-muted/30 px-3 text-sm text-foreground" value={customTurnoForm.ora_fine} onChange={e => setCustomTurnoForm(f => ({ ...f, ora_fine: e.target.value }))} data-testid="custom-turno-fine" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-muted-foreground mb-1 block">Reparto</label>
                  <select
                    className="w-full h-10 rounded-lg border border-border bg-muted/30 px-3 text-sm text-foreground"
                    value={customTurnoForm.reparto}
                    onChange={e => setCustomTurnoForm(f => ({ ...f, reparto: e.target.value }))}
                    data-testid="custom-turno-reparto"
                  >
                    <option value="tutti">Tutti i reparti</option>
                    <option value="cucina">Cucina</option>
                    <option value="sala">Sala</option>
                    <option value="laboratorio">Laboratorio</option>
                    <option value="altro">Altro</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-muted-foreground mb-1 block">Note</label>
                  <input type="text" className="w-full h-10 rounded-lg border border-border bg-muted/30 px-3 text-sm text-foreground" placeholder="Opzionale…" value={customTurnoForm.note} onChange={e => setCustomTurnoForm(f => ({ ...f, note: e.target.value }))} data-testid="custom-turno-note" />
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <button onClick={() => setDialogCustomTurno(false)} className="flex-1 py-3 rounded-xl bg-muted text-foreground font-medium text-sm" data-testid="custom-turno-cancel">Annulla</button>
                <button onClick={handleSalvaCustomTurno} disabled={salvandoCustom} className="flex-1 py-3 rounded-xl bg-primary text-white font-semibold text-sm disabled:opacity-50" data-testid="custom-turno-salva">
                  {salvandoCustom ? "Salvo…" : "Aggiungi"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog Copia Settimana Scorsa */}
      {copiaSettimanaDialog && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="copia-settimana-overlay">
          <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4">
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                  <Copy className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">Copia turni</h3>
                  <p className="text-xs text-muted-foreground">Conflitto in questa settimana</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-300">
                Stai per copiare <strong>{copiaSettimanaDialog.sorgente} turni</strong> dalla settimana scorsa,
                ma in questa settimana ci sono già <strong>{copiaSettimanaDialog.destinazione} turni</strong>. Come procedere?
              </p>

              <div className="flex flex-col gap-2 pt-1">
                <button
                  onClick={() => eseguiCopia("sovrascrivi")}
                  disabled={copiando}
                  className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm disabled:opacity-50 active:scale-[0.98] transition-all"
                  data-testid="copia-sovrascrivi-btn"
                >
                  Sovrascrivi tutto
                </button>
                <button
                  onClick={() => eseguiCopia("aggiungi")}
                  disabled={copiando}
                  className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm disabled:opacity-50 active:scale-[0.98] transition-all"
                  data-testid="copia-aggiungi-btn"
                >
                  Aggiungi solo i mancanti
                </button>
                <button
                  onClick={() => setCopiaSettimanaDialog(null)}
                  disabled={copiando}
                  className="w-full py-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium text-sm disabled:opacity-50"
                  data-testid="copia-annulla-btn"
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog Conflitto Turno */}
      {conflittoDialog && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="conflitto-turno-overlay">
          <div className="w-full max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4">
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Sovrapposizione turno</h3>
                  <p className="text-xs text-gray-500">{conflittoDialog.operatoreNome}</p>
                </div>
              </div>

              <p className="text-sm text-gray-600">
                <span className="font-semibold">{conflittoDialog.operatoreNome}</span> ha già il turno{" "}
                <span className="font-semibold text-red-600">
                  {conflittoDialog.conflitto.fascia_nome_conflitto} ({conflittoDialog.conflitto.ora_inizio_conflitto}-{conflittoDialog.conflitto.ora_fine_conflitto})
                </span>
                . Vuoi sostituirlo con{" "}
                <span className="font-semibold text-indigo-600">
                  {conflittoDialog.fasciaNew.nome} ({conflittoDialog.fasciaNew.ora_inizio}-{conflittoDialog.fasciaNew.ora_fine})
                </span>?
              </p>

              <div className="flex flex-col gap-2 pt-1">
                <button
                  onClick={() => handleRisolviConflitto('sostituisci')}
                  className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm active:scale-[0.98] transition-transform"
                  data-testid="conflitto-sostituisci-btn"
                >
                  Sostituisci con {conflittoDialog.fasciaNew.nome}
                </button>
                <button
                  onClick={() => handleRisolviConflitto('mantieni')}
                  className="w-full py-3 rounded-xl bg-gray-100 text-gray-700 font-medium text-sm active:scale-[0.98] transition-transform"
                  data-testid="conflitto-mantieni-btn"
                >
                  Mantieni {conflittoDialog.conflitto.fascia_nome_conflitto}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TurniMobile;
